import { useNavigate } from "react-router-dom";
import { useTheme } from '../hooks/ThemeContext';
import { useEffect, useState, useRef } from 'react';
import '../styles/GetDemoPage.css';
import * as Sentry from '@sentry/react';

function GetDemo() {
    const navigate = useNavigate();
    const [university, setUniversity] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [allColleges, setAllColleges] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const inputRef = useRef(null);

    const showError = (msg, ms = 5000) => {
        setErrorMsg(msg);
        setTimeout(() => setErrorMsg(''), ms);
    };

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Load college data for autocomplete
    useEffect(() => {
        fetch('/static/wuad.json')
            .then(res => res.ok ? res.json() : [])
            .then(data => {
                const collegeNames = data.map(college => college["name"]);
                setAllColleges(collegeNames);
            })
            .catch(() => setAllColleges([]));
    }, []);

    // Handle clicks outside suggestions dropdown
    useEffect(() => {
        const handleClick = (e) => {
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    const filterSuggestions = (value) => {
        if (!value.trim()) return [];
        const lowerVal = value.toLowerCase();
        const filtered = allColleges.filter(name =>
            name.toLowerCase().includes(lowerVal)
        );
        return [...filtered.slice(0, 8), 'Other / Not Listed'];
    };

    const handleUniversityChange = (e) => {
        const value = e.target.value;
        setUniversity(value);
        setSuggestions(filterSuggestions(value));
        setShowSuggestions(true);
    };

    const selectSuggestion = (suggestion) => {
        setUniversity(suggestion);
        setShowSuggestions(false);
        setSuggestions([]);
    };

    async function requestDemo(event) {
        event.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(event.target);

        // Validate required fields
        const name = formData.get('name')?.trim();
        const email = formData.get('email')?.trim();

        if (!university || !name || !email) {
            showError("Please complete all required fields.");
            setIsSubmitting(false);
            return;
        }

        // Simple email validation
        if (!/^.+@.+\..+$/.test(email)) {
            showError("Your email doesn't look quite right. Please check it before submitting.");
            setIsSubmitting(false);
            return;
        }

        // Add university to form data
        formData.set('university', university);

        try {
            const response = await fetch('/api/request_demo', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                if (!data.demo_request_sent) {
                    showError("Something went wrong. Please try again or email us directly.");
                } else {
                    setSubmitSuccess(true);
                    event.target.reset();
                    setUniversity('');
                }
            } else {
                showError("Something went wrong. Please try again.");
            }
        } catch (err) {
            Sentry.captureException(err);
            showError("Network error. Please check your connection and try again.");
        }

        setIsSubmitting(false);
    }

    if (submitSuccess) {
        return (
            <div className="demo-page">
                <div className="demo-container">
                    <div className="demo-success">
                        <div className="success-icon">✓</div>
                        <h2>Thank you!</h2>
                        <p>We've received your request and will be in touch within 24 hours to schedule your demo.</p>
                        <button
                            className="demo-btn secondary"
                            onClick={() => navigate('/')}
                        >
                            Back to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="demo-page">
            <button className="demo-back-btn" onClick={() => navigate('/')}>
                <span>←</span> Back
            </button>

            <div className="demo-container">
                <div className="demo-left">
                    <span className="demo-label">For Universities</span>
                    <h1 className="demo-title">
                        AI-powered internship matching for your students
                    </h1>
                    <p className="demo-subtitle">
                        Help students find relevant internships faster — without overloading your career center.
                    </p>

                    <div className="demo-value-props">
                        <div className="value-prop">
                            <span className="value-icon">🎓</span>
                            <div>
                                <h4>For domestic & international students</h4>
                                <p>Ensure international students only see H1B-eligible opportunities.</p>
                            </div>
                        </div>
                        <div className="value-prop">
                            <span className="value-icon">📊</span>
                            <div>
                                <h4>Real visibility into student engagement</h4>
                                <p>Career centers get data on actual usage — not guesswork.</p>
                            </div>
                        </div>
                        <div className="value-prop">
                            <span className="value-icon">🏫</span>
                            <div>
                                <h4>Custom-branded for your institution</h4>
                                <p>Your own themed portal with secure domain access.</p>
                            </div>
                        </div>
                    </div>

                    <div className="demo-pricing-hint">
                        <p><strong>Institution-wide or pilot pricing available.</strong></p>
                        <p className="subtle">University-wide 6-month pilot plans available.</p>
                    </div>
                </div>

                <div className="demo-right">
                    <div className="demo-form-card">
                        <h2>Request a Demo</h2>
                        <p className="form-subtitle">See how Rezify can help your students land internships.</p>

                        <form onSubmit={requestDemo}>
                            <div className="form-group" ref={inputRef}>
                                <label htmlFor="university">University</label>
                                <input
                                    type="text"
                                    id="university"
                                    name="university"
                                    value={university}
                                    onChange={handleUniversityChange}
                                    onFocus={() => university && setShowSuggestions(true)}
                                    placeholder="Start typing your institution name..."
                                    autoComplete="off"
                                    required
                                />
                                {showSuggestions && suggestions.length > 0 && (
                                    <ul className="suggestions-dropdown">
                                        {suggestions.map((suggestion, i) => (
                                            <li
                                                key={i}
                                                onClick={() => selectSuggestion(suggestion)}
                                                className={suggestion === 'Other / Not Listed' ? 'other-option' : ''}
                                            >
                                                {suggestion.split(new RegExp(`(${university})`, 'gi')).map((part, idx) =>
                                                    part.toLowerCase() === university.toLowerCase() ? (
                                                        <mark key={idx}>{part}</mark>
                                                    ) : (
                                                        <span key={idx}>{part}</span>
                                                    )
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Your Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder="Jane Smith"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Work Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="jane@university.edu"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="questions">Questions / Comments (optional)</label>
                                <textarea
                                    id="questions"
                                    name="questions"
                                    placeholder="Anything you'd like us to know before the demo?"
                                    rows={3}
                                />
                            </div>

                            <button
                                type="submit"
                                className="demo-btn primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Request Demo'}
                            </button>
                            {errorMsg && (
                                <div className="fail-popup" style={{ marginTop: '12px' }}>
                                    {errorMsg}
                                </div>
                            )}
                        </form>

                        <p className="form-footer">
                            We'll email you to schedule a 30-minute live demo with our team.
                            <strong> No cost and no obligation.</strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { GetDemo };